import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiService } from '../services/api';
import type {
  DateRangePreset,
  AnalyticsOverview,
  TrafficTimeseries,
  TrafficSources,
  TopPages,
  GeographyData,
  DeviceBreakdown,
  UserTypes,
  AnalyticsInsights,
  ActionLogEntry,
  ActionLogCreate,
  AnalyticsDigestEntry,
  Goal,
  GoalCreate,
  GoalUpdate,
  StrategyContext,
  StrategyHistoryEntry,
  InsightFeedback,
  InsightFeedbackCreate,
} from '../types/analytics';

interface AnalyticsState {
  // Date range
  dateRange: DateRangePreset;

  // Data
  overview: AnalyticsOverview | null;
  traffic: TrafficTimeseries | null;
  sources: TrafficSources | null;
  pages: TopPages | null;
  geography: GeographyData | null;
  devices: DeviceBreakdown | null;
  userTypes: UserTypes | null;

  // Insights & Action Log
  insights: AnalyticsInsights | null;
  actionLog: ActionLogEntry[];
  digests: AnalyticsDigestEntry[];

  // Strategy layer (#522)
  goals: Goal[];
  strategy: StrategyContext | null;
  strategyHistory: StrategyHistoryEntry[];
  insightFeedback: InsightFeedback[];

  // Loading flags
  overviewLoading: boolean;
  trafficLoading: boolean;
  sourcesLoading: boolean;
  pagesLoading: boolean;
  geographyLoading: boolean;
  devicesLoading: boolean;
  userTypesLoading: boolean;
  insightsLoading: boolean;
  actionLogLoading: boolean;
  digestsLoading: boolean;
  goalsLoading: boolean;
  strategyLoading: boolean;
  strategyHistoryLoading: boolean;
  insightFeedbackLoading: boolean;

  // Error
  error: string | null;

  // Actions
  setDateRange: (range: DateRangePreset) => void;
  loadAllData: () => Promise<void>;
  loadInsightsData: () => Promise<void>;
  loadStrategyLayer: () => Promise<void>;
  createActionLogEntry: (entry: ActionLogCreate) => Promise<void>;
  deleteActionLogEntry: (id: string) => Promise<void>;
  loadGoals: () => Promise<void>;
  createGoal: (payload: GoalCreate) => Promise<void>;
  updateGoal: (goalId: string, payload: GoalUpdate) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  loadStrategy: () => Promise<void>;
  updateStrategy: (content: string) => Promise<void>;
  loadStrategyHistory: () => Promise<void>;
  loadInsightFeedback: (weekStart: string) => Promise<void>;
  upsertInsightFeedback: (payload: InsightFeedbackCreate) => Promise<void>;
}

// Module-level set of insight_keys with an in-flight feedback upsert. Kept
// outside the Zustand state tree because it is a concurrency control, not
// UI data. Declared above the store so the action closure captures a
// defined reference.
const _inFlightFeedbackKeys = new Set<string>();

export const useAnalyticsStore = create<AnalyticsState>()(
  devtools(
    (set, get) => ({
      dateRange: '30d',

      overview: null,
      traffic: null,
      sources: null,
      pages: null,
      geography: null,
      devices: null,
      userTypes: null,

      insights: null,
      actionLog: [],
      digests: [],

      goals: [],
      strategy: null,
      strategyHistory: [],
      insightFeedback: [],

      overviewLoading: false,
      trafficLoading: false,
      sourcesLoading: false,
      pagesLoading: false,
      geographyLoading: false,
      devicesLoading: false,
      userTypesLoading: false,
      insightsLoading: false,
      actionLogLoading: false,
      digestsLoading: false,
      goalsLoading: false,
      strategyLoading: false,
      strategyHistoryLoading: false,
      insightFeedbackLoading: false,

      error: null,

      setDateRange: (range: DateRangePreset) => {
        set({ dateRange: range }, false, 'setDateRange');
        get().loadAllData();
      },

      loadAllData: async () => {
        const range = get().dateRange;
        set({ error: null }, false, 'loadAllData/start');

        // Launch all fetches in parallel
        const fetchers = [
          {
            key: 'overview' as const,
            loadingKey: 'overviewLoading' as const,
            fn: () => apiService.getAnalyticsOverview(range),
          },
          {
            key: 'traffic' as const,
            loadingKey: 'trafficLoading' as const,
            fn: () => apiService.getAnalyticsTraffic(range),
          },
          {
            key: 'sources' as const,
            loadingKey: 'sourcesLoading' as const,
            fn: () => apiService.getAnalyticsSources(range),
          },
          {
            key: 'pages' as const,
            loadingKey: 'pagesLoading' as const,
            fn: () => apiService.getAnalyticsPages(range),
          },
          {
            key: 'geography' as const,
            loadingKey: 'geographyLoading' as const,
            fn: () => apiService.getAnalyticsGeography(range),
          },
          {
            key: 'devices' as const,
            loadingKey: 'devicesLoading' as const,
            fn: () => apiService.getAnalyticsDevices(range),
          },
          {
            key: 'userTypes' as const,
            loadingKey: 'userTypesLoading' as const,
            fn: () => apiService.getAnalyticsUsers(range),
          },
        ];

        // Set all loading states
        const loadingState: Record<string, boolean> = {};
        for (const f of fetchers) {
          loadingState[f.loadingKey] = true;
        }
        set(loadingState as Partial<AnalyticsState>, false, 'loadAllData/loading');

        // Execute in parallel — each settles independently
        await Promise.allSettled(
          fetchers.map(async ({ key, loadingKey, fn }) => {
            try {
              const data = await fn();
              set(
                { [key]: data, [loadingKey]: false } as unknown as Partial<AnalyticsState>,
                false,
                `loadAllData/${key}/done`
              );
            } catch (err) {
              console.error(`Analytics fetch failed for ${key}:`, err);
              set(
                { [loadingKey]: false } as unknown as Partial<AnalyticsState>,
                false,
                `loadAllData/${key}/error`
              );
            }
          })
        );
      },

      loadInsightsData: async () => {
        set(
          { insightsLoading: true, actionLogLoading: true, digestsLoading: true },
          false,
          'loadInsightsData/start'
        );

        await Promise.allSettled([
          (async () => {
            try {
              const data = await apiService.getAnalyticsInsightsLatest();
              set({ insights: data, insightsLoading: false }, false, 'loadInsightsData/insights');
              // Feedback is scoped by week_start, so fetch it explicitly once
              // we know which week the insights row belongs to. Doing it here
              // rather than from a separate effect avoids a subtle race where
              // loadStrategyLayer fires before insights has settled and then
              // silently never retries.
              if (data?.week_start) {
                try {
                  const insightFeedback = await apiService.listAnalyticsInsightFeedback(
                    data.week_start
                  );
                  set(
                    { insightFeedback, insightFeedbackLoading: false },
                    false,
                    'loadInsightsData/feedback'
                  );
                } catch (err) {
                  console.error('Insight feedback fetch failed:', err);
                  set({ insightFeedbackLoading: false }, false, 'loadInsightsData/feedback/error');
                }
              }
            } catch (err) {
              console.error('Insights fetch failed:', err);
              set({ insightsLoading: false }, false, 'loadInsightsData/insights/error');
            }
          })(),
          (async () => {
            try {
              const data = await apiService.getAnalyticsActionLog();
              set(
                { actionLog: data, actionLogLoading: false },
                false,
                'loadInsightsData/actionLog'
              );
            } catch (err) {
              console.error('Action log fetch failed:', err);
              set({ actionLogLoading: false }, false, 'loadInsightsData/actionLog/error');
            }
          })(),
          (async () => {
            try {
              const data = await apiService.getAnalyticsDigests();
              set({ digests: data, digestsLoading: false }, false, 'loadInsightsData/digests');
            } catch (err) {
              console.error('Digests fetch failed:', err);
              set({ digestsLoading: false }, false, 'loadInsightsData/digests/error');
            }
          })(),
        ]);
      },

      createActionLogEntry: async (entry: ActionLogCreate) => {
        const created = await apiService.createAnalyticsActionLogEntry(entry);
        set(
          (state) => ({ actionLog: [created, ...state.actionLog] }),
          false,
          'createActionLogEntry'
        );
      },

      deleteActionLogEntry: async (id: string) => {
        await apiService.deleteAnalyticsActionLogEntry(id);
        set(
          (state) => ({ actionLog: state.actionLog.filter((e) => e.id !== id) }),
          false,
          'deleteActionLogEntry'
        );
      },

      // ---- Strategy layer (#522) ----

      // Loads goals + strategy in parallel. Feedback is fetched by
      // loadInsightsData once the insights row's week_start is known — don't
      // duplicate that here.
      loadStrategyLayer: async () => {
        set(
          {
            goalsLoading: true,
            strategyLoading: true,
          },
          false,
          'loadStrategyLayer/start'
        );

        await Promise.allSettled([
          (async () => {
            try {
              const goals = await apiService.listAnalyticsGoals();
              set({ goals, goalsLoading: false }, false, 'loadStrategyLayer/goals');
            } catch (err) {
              console.error('Goals fetch failed:', err);
              set({ goalsLoading: false }, false, 'loadStrategyLayer/goals/error');
            }
          })(),
          (async () => {
            try {
              const strategy = await apiService.getAnalyticsStrategy();
              set({ strategy, strategyLoading: false }, false, 'loadStrategyLayer/strategy');
            } catch (err) {
              console.error('Strategy fetch failed:', err);
              set({ strategyLoading: false }, false, 'loadStrategyLayer/strategy/error');
            }
          })(),
        ]);
      },

      loadGoals: async () => {
        set({ goalsLoading: true }, false, 'loadGoals/start');
        try {
          const goals = await apiService.listAnalyticsGoals();
          set({ goals, goalsLoading: false }, false, 'loadGoals/done');
        } catch (err) {
          console.error('Goals fetch failed:', err);
          set({ goalsLoading: false }, false, 'loadGoals/error');
          throw err;
        }
      },

      createGoal: async (payload: GoalCreate) => {
        const created = await apiService.createAnalyticsGoal(payload);
        set((state) => ({ goals: [created, ...state.goals] }), false, 'createGoal');
      },

      updateGoal: async (goalId: string, payload: GoalUpdate) => {
        const updated = await apiService.updateAnalyticsGoal(goalId, payload);
        set(
          (state) => ({
            goals: state.goals.map((g) => (g.id === goalId ? updated : g)),
          }),
          false,
          'updateGoal'
        );
      },

      deleteGoal: async (goalId: string) => {
        await apiService.deleteAnalyticsGoal(goalId);
        set(
          (state) => ({ goals: state.goals.filter((g) => g.id !== goalId) }),
          false,
          'deleteGoal'
        );
      },

      loadStrategy: async () => {
        set({ strategyLoading: true }, false, 'loadStrategy/start');
        try {
          const strategy = await apiService.getAnalyticsStrategy();
          set({ strategy, strategyLoading: false }, false, 'loadStrategy/done');
        } catch (err) {
          console.error('Strategy fetch failed:', err);
          set({ strategyLoading: false }, false, 'loadStrategy/error');
          throw err;
        }
      },

      updateStrategy: async (content: string) => {
        const updated = await apiService.updateAnalyticsStrategy({ content });
        set({ strategy: updated }, false, 'updateStrategy');
      },

      loadStrategyHistory: async () => {
        set({ strategyHistoryLoading: true }, false, 'loadStrategyHistory/start');
        try {
          const strategyHistory = await apiService.listAnalyticsStrategyHistory();
          set(
            { strategyHistory, strategyHistoryLoading: false },
            false,
            'loadStrategyHistory/done'
          );
        } catch (err) {
          console.error('Strategy history fetch failed:', err);
          set({ strategyHistoryLoading: false }, false, 'loadStrategyHistory/error');
          throw err;
        }
      },

      loadInsightFeedback: async (weekStart: string) => {
        set({ insightFeedbackLoading: true }, false, 'loadInsightFeedback/start');
        try {
          const insightFeedback = await apiService.listAnalyticsInsightFeedback(weekStart);
          set(
            { insightFeedback, insightFeedbackLoading: false },
            false,
            'loadInsightFeedback/done'
          );
        } catch (err) {
          console.error('Insight feedback fetch failed:', err);
          set({ insightFeedbackLoading: false }, false, 'loadInsightFeedback/error');
          throw err;
        }
      },

      upsertInsightFeedback: async (payload: InsightFeedbackCreate) => {
        // Guard against rapid double-clicks on the same insight: skip the
        // request entirely if one is already in flight for this key, so we
        // can't end up with two concurrent filter+prepend writes racing each
        // other into a momentarily-duplicated state.
        if (_inFlightFeedbackKeys.has(payload.insight_key)) return;
        _inFlightFeedbackKeys.add(payload.insight_key);
        try {
          const created = await apiService.upsertAnalyticsInsightFeedback(payload);
          set(
            (state) => {
              const others = state.insightFeedback.filter(
                (f) => f.insight_key !== created.insight_key
              );
              return { insightFeedback: [created, ...others] };
            },
            false,
            'upsertInsightFeedback'
          );
        } finally {
          _inFlightFeedbackKeys.delete(payload.insight_key);
        }
      },
    }),
    { name: 'analytics-store' }
  )
);
